import { captureBattleTiming } from "./dispatch";
import {
    Effect,
    RoundEffect,
    ConsumeEffect,
    免疫攻击,
    闪避,
    秒杀,
    增伤,
    减伤,
    保伤,
} from "./effect";
import type { Context } from "./cond";
import { ActionContext, type BattleEvent, type UseSkillResult } from "./events";
import { AttackSkill, Skill } from "./skill";
import { AbnormalState } from "./state";
import { Timing } from "./timing";
import type { Operation, Side } from "./operation";
import type { Pet } from "./pet";
import type { Seer } from "./seer";

export type { UseSkillResult } from "./events";

/** 单方行动。Round 负责出手开始、结束与阵亡边界，Move 负责技能阶段。 */
export class Move {
    /**
     * lastUseSkillResult，保存当前实例的lastUseSkillResult状态。
     */
    lastUseSkillResult?: UseSkillResult;

    /**
     * 创建实例并初始化所需的领域状态。
     * @param operation operation参数，参与本次操作
     */
    constructor(public operation: Operation) {}

    /**
     * checkSkillUsable：执行对应的领域操作。
     * @param pet 目标精灵
     * @param skill 本次使用的技能
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    checkSkillUsable(pet: Pet, skill: Skill): UseSkillResult {
        /**
         * restricted，供本模块相关规则使用。
         */
        const restricted = pet.异常状态.find(
            (state) => state instanceof AbnormalState && state.isRestricted,
        );
        if (restricted) return { used: false, blockReason: restricted.name };
        if (skill.curPP <= 0) return { used: false, blockReason: "PP已耗尽" };
        return { used: true };
    }

    /**
     * useSkill：执行对应的领域操作。
     * @param sbjPet 效果持有者精灵
     * @param skill 本次使用的技能
     * @param objPet 效果作用目标精灵
     * @param sbjSeer sbjSeer参数，参与本次操作
     * @param objSeer objSeer参数，参与本次操作
     * @param side 操作所属的一方；默认值为 `"left"`
     * @param events 接收本次产生事件的数组；默认值为 `[]`
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    useSkill(
        sbjPet: Pet,
        skill: Skill,
        objPet: Pet,
        sbjSeer: Seer,
        objSeer: Seer,
        side: Side = "left",
        events: BattleEvent[] = [],
    ): UseSkillResult {
        /**
         * usable，供本模块相关规则使用。
         */
        const usable = this.checkSkillUsable(sbjPet, skill);
        if (!usable.used) return (this.lastUseSkillResult = usable);
        /**
         * action，供本模块相关规则使用。
         */
        const action = new ActionContext();
        /**
         * damageScale，供本模块相关规则使用。
         */
        let damageScale = 1;
        let minDamage = 0;
        /**
         * collectingDamage，供本模块相关规则使用。
         */
        let collectingDamage = true;
        /**
         * context，供本模块相关规则使用。
         */
        const context: Context = {
            sbjPet,
            objPet,
            sbjSeer,
            objSeer,
            skill,
            action,
            side,
            events,
            onEffectResult: (effect, result, ownerContext) => {
                if (!collectingDamage || typeof result !== "number") return;
                if (effect instanceof 增伤 && ownerContext.sbjPet === sbjPet)
                    damageScale *= 1 + result / 100;
                if (effect instanceof 保伤 && ownerContext.sbjPet === sbjPet)
                    minDamage = Math.max(minDamage, result);
                if (effect instanceof 减伤 && ownerContext.sbjPet === objPet)
                    damageScale *= 1 - result / 100;
            },
        };
        /**
         * resolveDamageScale，供本模块相关规则使用。
         */
        const resolveDamageScale = () => {
            collectingDamage = false;
            return { damageScale, minDamage };
        };
        /**
         * prepare，供本模块相关规则使用。
         */
        const prepare = (timing: Timing) => {
            events.push({ timing, side });
            /**
             * hooks，供本模块相关规则使用。
             */
            const hooks = captureBattleTiming(
                timing,
                { ...context, timing },
                skill.getEffectsAt(timing),
            );
            // 额外魂印观察者也参与同一命中分段，不能绕过封攻、闪避和师盔门。
            return {
                attacker: (filter?: Parameters<typeof hooks.attacker>[0]) => hooks.attacker(filter),
                defender: (filter?: Parameters<typeof hooks.defender>[0]) => {
                    hooks.defender(filter);
                    hooks.observers(filter);
                },
            };
        };
        /**
         * phase，供本模块相关规则使用。
         */
        const phase = (timing: Timing, defenderFirst = false) => {
            /**
             * hooks，供本模块相关规则使用。
             */
            const hooks = prepare(timing);
            if (defenderFirst) {
                hooks.defender();
                hooks.attacker();
            } else {
                hooks.attacker();
                hooks.defender();
            }
        };

        // PP 在使用技能时立刻扣除，命中失败也不会返还。
        skill.curPP--;
        phase(Timing.onUseSkill);
        /**
         * hitHooks，供本模块相关规则使用。
         */
        const hitHooks = prepare(Timing.beforeSkillHit);
        /**
         * registration，供本模块相关规则使用。
         */
        const registration = (effect: Effect, definition: boolean) =>
            definition &&
            (effect instanceof RoundEffect ||
                effect instanceof ConsumeEffect);
        /**
         * preparation，供本模块相关规则使用。
         */
        const preparation = (effect: Effect, definition: boolean) =>
            registration(effect, definition) ||
            (!(effect.effect instanceof 免疫攻击) && !(effect.effect instanceof 闪避));
        hitHooks.attacker(preparation);
        hitHooks.defender(preparation);
        /**
         * result，供本模块相关规则使用。
         */
        const result: UseSkillResult = { used: true, hit: false, hitEffectsApplied: false };
        if (action.sealed) result.missReason = "sealed";
        else if (
            !skill.sureHit &&
            sbjSeer.random.next() >=
                this.getSkillHitRate(sbjPet.effectiveLevel("hit", objPet))
        ) {
            result.missReason = "accuracy";
        } else {
            if (!skill.sureHit) {
                hitHooks.defender((effect) => effect.effect instanceof 闪避);
                const evasion = this.getEvasionRate(
                    objPet.effectiveLevel("speed", sbjPet),
                    sbjPet.effectiveLevel("hit", objPet),
                    action.evasionRates,
                );
                if (sbjSeer.random.next() < evasion) result.missReason = "evasion";
            }
            if (!result.missReason) {
                // 前三段通过才检查师盔；封攻、命中失败与闪避不会提前消耗免疫次数。
                hitHooks.defender((effect) => effect.effect instanceof 免疫攻击);
                if (
                    skill instanceof AttackSkill &&
                    action.attackImmune &&
                    !action.ignoreAttackImmunity
                )
                    result.missReason = "attackImmune";
                else result.hit = true;
            }
        }

        objPet.战斗面板.notHit = !result.hit;
        objPet.战斗面板.attackImmune = result.missReason === "attackImmune";
        if (result.hit) {
            // 防守方先建立命中附效失效标志，随后才派发当前技能的命中效果。
            phase(Timing.onSkillHit, true);
            result.hitEffectsApplied = !action.hitEffectsInvalid;
            if (skill instanceof AttackSkill) {
                /**
                 * damage，供本模块相关规则使用。
                 */
                const damage = this.dealDamage(skill, context, phase, prepare, resolveDamageScale);
                result.damage = damage;
                result.criticalHit =
                    sbjPet.criticalStrike === true && damage > 0;
            }
        } else phase(Timing.onSkillMiss);
        phase(Timing.afterUseSkill);
        return (this.lastUseSkillResult = result);
    }

    /**
     * dealDamage：执行对应的领域操作。
     * @param skill 本次使用的技能
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @param phase phase参数，参与本次操作
     * @param prepare prepare参数，参与本次操作
     * @param resolveDamageScale resolveDamageScale参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    private dealDamage(
        skill: AttackSkill,
        context: Context,
        phase: (timing: Timing) => void,
        prepare: (timing: Timing) => {
            /**
             * attacker，保存当前实例的attacker状态。
             */
            attacker: ReturnType<Pet["captureTiming"]>;
            /**
             * defender，保存当前实例的defender状态。
             */
            defender: ReturnType<Pet["captureTiming"]>;
        },
        resolveDamageScale: () => { damageScale: number; minDamage: number },
    ): number {
        /**
         * { sbjPet, objPet, sbjSeer, objSeer, action }，供本模块相关规则使用。
         */
        const { sbjPet, objPet, sbjSeer, objSeer, action } = context;
        phase(Timing.beforeDamage);
        /**
         * hooks，供本模块相关规则使用。
         */
        const hooks = prepare(Timing.onDamage);
        /**
         * baseDamage，供本模块相关规则使用。
         */
        const baseDamage = skill.getBaseDamage(sbjPet, objPet, sbjSeer, objSeer);
        if (!sbjPet.criticalStrike && skill.criticalStrikeValue > 0) {
            sbjPet.criticalStrike = sbjSeer.random.next() < skill.criticalStrikeValue / 16;
        }
        /**
         * instakill，供本模块相关规则使用。
         */
        const instakill = (effect: Effect) => effect.effect instanceof 秒杀;
        hooks.attacker(instakill);
        hooks.defender(instakill);
        /**
         * damageScale，供本模块相关规则使用。
         */
        const { damageScale, minDamage } = resolveDamageScale();
        /**
         * damage，供本模块相关规则使用。
         */
        const damage = sbjPet.causeDamage(
            baseDamage * (sbjPet.criticalStrike ? 2 : 1),
            objPet,
            sbjPet.addon,
            damageScale,
            sbjPet.criticalStrike,
            minDamage,
        );
        objPet.战斗面板.damage = damage;
        // 普通伤害后，秒杀将剩余体力直接流失；不计入伤害或吸血基数。
        if (action?.instakill) {
            const loss = objPet.loseHp(objPet.当前体力);
            if (loss > 0) {
                objPet.战斗面板.hpLoss = (objPet.战斗面板.hpLoss ?? 0) + loss;
            }
        }
        // 此阶段已应用普通伤害和秒杀流失，再结算吸血、追加伤害等附效。
        hooks.attacker((effect) => !instakill(effect));
        hooks.defender((effect) => !instakill(effect));
        return damage;
    }

    /**
     * getSkillHitRate：执行对应的领域操作。
     * @param baseHitRate baseHitRate参数，参与本次操作
     * @param hitLevel hitLevel参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    getSkillHitRate(hitLevel: number): number {
        return 1 - Math.min(6, Math.max(0, -hitLevel)) * 0.15;
    }

    /** 加算闪避先封顶，再扣除命中提升提供的百分点。 */
    getEvasionRate(speedLevel: number, hitLevel: number, effectRates: number[] = []): number {
        const speed = Math.min(6, Math.max(0, speedLevel)) * 0.05;
        const total = Math.min(1, speed + effectRates.reduce((sum, rate) => sum + rate, 0));
        return Math.max(0, total - Math.min(6, Math.max(0, hitLevel)) * 0.1);
    }
}
